import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  adjustArrayFormatForRequestParams,
  isParameterFiltered,
} from '@shared/http/http-param-filter';
import { environment } from 'environments/environment';
import { omitBy } from 'lodash-es';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CalibrationLawMethods } from '../components/charge-port/charge-port-config/data/charge-port-config.data';
import { UpdateStationLocation } from '../components/charging-station/charging-station-address/data/charging-station-address.data';
import {
  UpdateLg2wanCommunicationDevice,
  UpdateOcppCommunicationDevice,
  UpdateStationDeviceDetails,
} from '../components/charging-station/charging-station-device-detail/data/charging-station-device-detail.data';
import { StationInfoUpdatePayload } from '../components/charging-station/charging-station-info/data/charging-station-info.data';
import {
  FirmwareFile,
  Model,
  UploadFirmwareRequest,
  UploadFirmwareResponse,
  Vendor,
  Version,
} from '../components/charging-station/overlays/charging-station-firmware-update/data/charging-station-firmware-update.component.data';
import { StationProviderUpdate } from '../components/charging-station/overlays/charging-station-provider-change/data/charging-station-provider.data';
import {
  AllChargePortStatus,
  ChargingStationLogFile,
  DownloadId,
  StationFirmwareAssignment,
} from '../model/charging-infrastructure-detail.model';
import {
  ChargingStation,
  ChargingStationConnectionStatus,
  ChargingStationState,
  Country,
  MobileNetworkDetails,
  Providers,
  SimIdType,
  UninstallReason,
} from '../model/charging-station.model';
import {
  AggregatedCommunicationLogs,
  CommunicationLogsFilter,
  CommunicationsLogsFilterValues,
} from '../model/communication-logs.model';
import { ExternalRoamingPreRequirements } from '../model/external-roaming-pre-requirements.model';
import { LocalAccessInfo } from './charging-station-access.service';

@Injectable({
  providedIn: 'root',
})
export class ChargingStationService {
  private readonly httpClient = inject(HttpClient);

  stationInformation(chargingStationUuid: string): Observable<ChargingStation> {
    const params = new HttpParams().set('refresh', 'true');
    return this.httpClient.get<ChargingStation>(
      environment.bffChargingStationOperations + '/charging-stations/' + chargingStationUuid,
      { params }
    );
  }

  statusOfAllPorts(chargingStationUuid: string): Observable<AllChargePortStatus> {
    return this.httpClient.get<AllChargePortStatus>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        '/status'
    );
  }

  calibrationLawMethodsOptions(stationUuid: string): Observable<string[]> {
    return this.httpClient
      .get<CalibrationLawMethods>(
        environment.bffChargingStationOperations +
          '/charging-stations/' +
          stationUuid +
          '/calibration-law-methods'
      )
      .pipe(map(response => response.calibrationLawMethods));
  }

  updateDeviceDetails(
    chargingStationUuid: string,
    deviceDetails: UpdateStationDeviceDetails
  ): Observable<Response> {
    return this.httpClient.put<Response>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        '/device-details',
      deviceDetails
    );
  }

  updateOcppCommunicationDevice(
    chargingStationUuid: string,
    deviceId: string,
    communicationDevice: UpdateOcppCommunicationDevice
  ): Observable<Response> {
    return this.httpClient.put<Response>(
      `${environment.bffChargingStationOperations}/charging-stations/${chargingStationUuid}/communication-devices/${deviceId}`,
      communicationDevice
    );
  }

  updateLg2WanCommunicationDevice(
    chargingStationUuid: string,
    portUuid: string,
    communicationDevice: UpdateLg2wanCommunicationDevice
  ): Observable<Response> {
    return this.httpClient.put<Response>(
      `${environment.bffChargingStationOperations}/charging-stations/${chargingStationUuid}/charge-ports/${portUuid}/communication-device`,
      communicationDevice
    );
  }

  updateStationPassword(chargingStationUuid: string, password: string): Observable<Response> {
    return this.httpClient.post<Response>(
      `${environment.bffChargingStationOperations}/charging-stations/${chargingStationUuid}/password`,
      {
        password,
      }
    );
  }

  updateLocation(
    chargingStationUuid: string,
    location: UpdateStationLocation
  ): Observable<Response> {
    return this.httpClient.put<Response>(
      `${environment.bffChargingStationOperations}/charging-stations/${chargingStationUuid}/address`,
      location
    );
  }

  updateStationInfo(
    chargingStationUuid: string,
    info: StationInfoUpdatePayload
  ): Observable<Response> {
    return this.httpClient.put<Response>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        '/infos',
      info
    );
  }

  logFiles(
    chargingStationUuid: string,
    chargePortUuid: string
  ): Observable<ChargingStationLogFile[]> {
    return this.httpClient
      .get<{
        logs: ChargingStationLogFile[];
      }>(
        environment.bffChargingStationOperations +
          '/charging-stations/' +
          chargingStationUuid +
          '/charge-ports/' +
          chargePortUuid +
          '/logs'
      )
      .pipe(map(response => response.logs));
  }

  fetchNewLogFile(chargingStationUuid: string, chargePortUuid: string): Observable<Response> {
    return this.httpClient.post<Response>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        '/charge-ports/' +
        chargePortUuid +
        '/logs/refresh',
      {}
    );
  }

  downloadLogFile(
    chargingStationUuid: string,
    chargePortUuid: string,
    fileName: string
  ): Observable<Blob> {
    return this.httpClient.get<Blob>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        '/charge-ports/' +
        chargePortUuid +
        '/logs/' +
        fileName,
      { responseType: <any>'blob' }
    );
  }

  determineDownloadId(chargingStationUuid: string, fileName: string): Observable<DownloadId> {
    return this.httpClient.get<DownloadId>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        '/logs/determine-download-id/' +
        fileName
    );
  }

  getStationVendors(chargingStationUuid: string): Observable<Vendor[]> {
    return this.httpClient.get<Vendor[]>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        '/firmware/vendors'
    );
  }

  getStationModelsByVendor(chargingStationUuid: string, vendor: string): Observable<Model[]> {
    return this.httpClient.get<Model[]>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        `/firmware/vendors/${vendor}/models`
    );
  }

  getFirmwareVersionByModel(
    chargingStationUuid: string,
    vendor: string,
    model: string
  ): Observable<Version[]> {
    return this.httpClient.get<Version[]>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        `/firmware/vendors/${vendor}/models/${model}/versions`
    );
  }

  getFirmwareByVersion(
    chargingStationUuid: string,
    vendor: string,
    model: string,
    version: string
  ): Observable<FirmwareFile[]> {
    return this.httpClient.get<FirmwareFile[]>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        `/firmware/vendors/${vendor}/models/${model}/versions/${version}/firmware-files`
    );
  }

  /**
   * In case of ocpp 1.6 a firmware rollout group will be created/updated and id gets returned
   *
   * In case of ocpp 1.5 no firmware rollout will be created and nothing returned
   */
  uploadFirmware(
    chargingStationUuid: string,
    request: UploadFirmwareRequest
  ): Observable<UploadFirmwareResponse> {
    return this.httpClient.put<UploadFirmwareResponse>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        '/firmware',
      request
    );
  }

  externalRoamingPreRequirements(
    chargingStationUuid: string
  ): Observable<ExternalRoamingPreRequirements> {
    return this.httpClient.get<ExternalRoamingPreRequirements>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        '/accessibility/external-roaming-pre-requirements'
    );
  }

  getBasedataCountries(): Observable<Country[]> {
    return this.httpClient.get<Country[]>(
      environment.bffChargingStationOperations + '/basedata/location/countries'
    );
  }

  getChargingStationAggregatedCommunicationLogs(
    stationUuid: string,
    filterSet: CommunicationLogsFilter
  ): Observable<AggregatedCommunicationLogs> {
    const params = new HttpParams({
      fromObject: adjustArrayFormatForRequestParams(omitBy(filterSet, p => isParameterFiltered(p))),
    });
    return this.httpClient.get<AggregatedCommunicationLogs>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        stationUuid +
        '/communication-logs/aggregated',
      { params }
    );
  }

  getCommunicationLogsFilterValues(
    stationUuid: string
  ): Observable<CommunicationsLogsFilterValues> {
    return this.httpClient.get<CommunicationsLogsFilterValues>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        stationUuid +
        '/communication-logs/filters'
    );
  }

  putChargingStationIntoOperation(
    stationUuid: string,
    entityVersion: number
  ): Observable<Response> {
    return this.httpClient.put<Response>(
      environment.bffChargingStationOperations + '/charging-stations/' + stationUuid + '/state',
      { chargingStationState: ChargingStationState.IBET, entityVersion }
    );
  }

  uninstallChargingStation(
    stationUuid: string,
    entityVersion: number,
    uninstallReason: UninstallReason,
    explanation?: string
  ): Observable<Response> {
    return this.httpClient.put<Response>(
      environment.bffChargingStationOperations + '/charging-stations/' + stationUuid + '/state',
      {
        chargingStationState: ChargingStationState.ABET,
        entityVersion,
        uninstallReason,
        ...(uninstallReason === UninstallReason.OTHER && { explanation }),
      }
    );
  }

  triggerMessage(
    uuid: string,
    requestedMessage: string,
    connectorId: number
  ): Observable<Response> {
    return this.httpClient.post<Response>(
      environment.bffChargingStationOperations + '/charging-stations/' + uuid + '/trigger-message',
      { requestedMessage, connectorId }
    );
  }

  fetchStationRolloutAssignment(stationUuid: string): Observable<StationFirmwareAssignment> {
    return this.httpClient.get<StationFirmwareAssignment>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        stationUuid +
        '/firmware/assigned-rollout'
    );
  }

  getManufacturers(): Observable<{ manufacturers: string[] }> {
    return this.httpClient.get<{ manufacturers: string[] }>(
      environment.bffChargingStationOperations + '/basedata/manufacturers'
    );
  }

  getModelInfos(manufacturer: string): Observable<{ modelInfos: string[] }> {
    return this.httpClient.get<{ modelInfos: string[] }>(
      environment.bffChargingStationOperations +
        `/basedata/manufacturers/${manufacturer}/model-infos`
    );
  }

  getProviders(): Observable<Providers> {
    return this.httpClient.get<Providers>(
      `${environment.bffChargingStationOperations}/basedata/chargeport-operators`
    );
  }

  updateProviderOfStation(stationId: string, payload: StationProviderUpdate): Observable<Response> {
    return this.httpClient.put<Response>(
      `${environment.bffChargingStationOperations}/charging-stations/${stationId}/chargeport-operator`,
      payload
    );
  }

  getLocationTypes(): Observable<string[]> {
    return this.httpClient.get<string[]>(
      `${environment.bffChargingStationOperations}/basedata/location-types`
    );
  }

  getLlmWebAppLink(stationId: string): Observable<string> {
    return this.httpClient
      .get<{
        uuid: string;
        url: string;
      }>(`${environment.bffChargingStationOperations}/charging-stations/${stationId}/llm-route`)
      .pipe(map(res => res.url));
  }

  touchStation(stationId: string): Observable<Response> {
    return this.httpClient.put<Response>(
      `${environment.bffChargingStationOperations}/charging-stations/${stationId}/touch`,
      null
    );
  }

  checkMeterCalibration(evseName: string): Observable<boolean> {
    return this.httpClient
      .get<string>(`${environment.calibrationLawCheckService}?ladepunkt=${evseName}`, {
        responseType: <any>'text',
      })
      .pipe(
        map(
          response => response.includes('<img') // valid if an image exists
        )
      );
  }

  getLg2wanControllerIds(): Observable<{ controllerIds: string[] }> {
    return this.httpClient.get<{ controllerIds: string[] }>(
      `${environment.bffChargingStationOperations}/basedata/lg2wan-gateways`
    );
  }

  getStationNetworkDetails(simId: string, simIdType: SimIdType): Observable<MobileNetworkDetails> {
    const params = new HttpParams({
      fromObject: {
        simId,
        simIdType,
      },
    });
    return this.httpClient.get<MobileNetworkDetails>(
      `${environment.bffChargingStationOperations}/sim-cards`,
      { params }
    );
  }

  refreshStationNetworkDetails(simId: string, simIdType: SimIdType): Observable<Response> {
    const body = {
      iccids: simIdType === SimIdType.ICCID ? [simId] : [],
      imsis: simIdType === SimIdType.IMSI ? [simId] : [],
    };
    return this.httpClient.post<Response>(
      `${environment.bffChargingStationOperations}/sim-cards/refresh`,
      body
    );
  }

  deleteChargingStation(stationUuid: string): Observable<Response> {
    return this.httpClient.delete<Response>(
      `${environment.bffChargingStationOperations}/charging-stations/${stationUuid}`
    );
  }

  getChargingStationConnectionStatus(
    stationUuid: string
  ): Observable<ChargingStationConnectionStatus> {
    return this.httpClient.get<ChargingStationConnectionStatus>(
      `${environment.bffChargingStationOperations}/charging-stations/${stationUuid}/connection-status`
    );
  }

  setStationToAcceptAnyPassword(stationUuid: string): Observable<{ validUntil: string }> {
    return this.httpClient.post<{ validUntil: string }>(
      `${environment.bffChargingStationOperations}/charging-stations/${stationUuid}/accept-any-password`,
      {}
    );
  }

  refreshOcppConfiguration(chargingStationUuid: string): Observable<Response | boolean> {
    return this.httpClient.post<Response>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        '/effective-config',
      ''
    );
  }

  refreshLg2WanConfigurationByPortId(
    chargingStationUuid: string,
    chargePortUuid: string
  ): Observable<Response | boolean> {
    return this.httpClient.post<Response>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        chargingStationUuid +
        '/charge-ports/' +
        chargePortUuid +
        '/effective-config',
      ''
    );
  }

  getLocalAccessUrls(stationUuid: string): Observable<LocalAccessInfo[]> {
    return this.httpClient.get<LocalAccessInfo[]>(
      environment.bffChargingStationOperations +
        '/charging-stations/' +
        stationUuid +
        '/local-access-urls'
    );
  }
}
